import { useEffect, useRef, useState } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";

import { Button } from "../button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../dialog";
import { cn } from "../../lib/utils";

interface DeleteAccountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  isDeleting?: boolean;
}

export function DeleteAccountDialog({
  open,
  onOpenChange,
  onConfirm,
  isDeleting = false,
}: DeleteAccountDialogProps) {
  const [progress, setProgress] = useState(0);
  const [isHolding, setIsHolding] = useState(false);
  const animationRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  const handleStart = (e: React.MouseEvent | React.TouchEvent) => {
    if (isDeleting) return;
    if (e.cancelable) e.preventDefault();
    setIsHolding(true);
    startTimeRef.current = Date.now();

    const update = () => {
      if (!startTimeRef.current) return;
      const elapsed = Date.now() - startTimeRef.current;
      const currentProgress = Math.min(100, (elapsed / 2000) * 100);
      setProgress(currentProgress);

      if (currentProgress < 100) {
        animationRef.current = requestAnimationFrame(update);
      } else {
        setIsHolding(false);
        setProgress(0);
        onConfirm();
      }
    };

    animationRef.current = requestAnimationFrame(update);
  };

  const handleEnd = () => {
    setIsHolding(false);
    startTimeRef.current = null;
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }

    const drainStart = Date.now();
    const startProgress = progress;

    const drain = () => {
      const elapsed = Date.now() - drainStart;
      const currentProgress = Math.max(
        0,
        startProgress - (elapsed / 300) * startProgress
      );
      setProgress(currentProgress);
      if (currentProgress > 0) {
        animationRef.current = requestAnimationFrame(drain);
      }
    };
    animationRef.current = requestAnimationFrame(drain);
  };

  const handleClose = (nextOpen: boolean) => {
    if (!nextOpen) {
      handleEnd();
      setProgress(0);
    }
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md border-red-200">
        <DialogHeader>
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
            <AlertTriangle className="h-6 w-6 animate-pulse text-red-600" />
          </div>
          <DialogTitle className="text-center text-xl font-semibold text-red-700">
            Delete your account?
          </DialogTitle>
          <DialogDescription className="text-muted-foreground mt-2 text-center text-sm">
            This action is{" "}
            <span className="text-foreground font-semibold">permanent</span> and
            cannot be undone. All of your data will be deleted forever.
          </DialogDescription>
        </DialogHeader>

        <div className="my-6 flex flex-col items-center justify-center space-y-4">
          <p className="text-muted-foreground text-center text-xs font-medium">
            {isHolding
              ? "Keep holding to confirm..."
              : "Press and hold the button below to confirm"}
          </p>

          <button
            type="button"
            onMouseDown={handleStart}
            onMouseUp={handleEnd}
            onMouseLeave={handleEnd}
            onTouchStart={handleStart}
            onTouchEnd={handleEnd}
            disabled={isDeleting}
            className={cn(
              "bg-background relative flex h-14 w-full items-center justify-center overflow-hidden rounded-lg border-2 border-red-600 font-semibold text-red-600 transition-all duration-200 select-none focus:outline-none disabled:cursor-not-allowed disabled:opacity-50",
              isHolding ? "scale-[0.98] shadow-inner" : "hover:bg-red-50"
            )}
          >
            <div
              className="absolute top-0 bottom-0 left-0 bg-red-600 transition-all duration-75"
              style={{
                width: `${progress}%`,
                opacity: 0.15 + (progress / 100) * 0.85,
              }}
            />
            <span
              className={cn(
                "pointer-events-none relative z-10 flex items-center justify-center gap-2 text-sm transition-colors duration-200",
                progress > 50 ? "text-white" : "text-red-600"
              )}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Deleting account...
                </>
              ) : isHolding ? (
                `Confirming delete (${Math.max(0, 2 - (progress / 100) * 2).toFixed(1)}s)`
              ) : (
                "Hold to delete account"
              )}
            </span>
          </button>
        </div>

        <DialogFooter className="gap-2 sm:justify-center">
          <Button
            variant="outline"
            disabled={isDeleting}
            onClick={() => handleClose(false)}
            className="w-full sm:w-auto"
          >
            Cancel, keep account
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
